import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { ReferralRequest } from '../../lib/api/referrals';
import { getTimeAgo } from '../../utils/timeAgo';

interface ReferralRequestCardProps {
  request: ReferralRequest;
  onPress: () => void;
  showSubmitButton?: boolean;
  onSubmitIntro?: () => void;
  showEditButton?: boolean;
  onEdit?: () => void;
  showIntrosReceived?: boolean;
}

const ReferralRequestCard: React.FC<ReferralRequestCardProps> = ({ 
  request, 
  onPress, 
  showSubmitButton = false,
  onSubmitIntro,
  showEditButton = false,
  onEdit,
  showIntrosReceived = false,
}) => {
  const { theme } = useTheme();

  const hasResponded = request.responses && request.responses.length > 0;

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {/* Header with user info */}
      <View style={styles.header}>
        <View style={styles.userInfo}>
          {request.user.photo ? (
            <Image
              source={{ uri: request.user.photo }}
              style={styles.avatar}
              contentFit="cover"
            />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: theme.accent + '20' }]}>
              <Ionicons name="person" size={20} color={theme.accent} />
            </View>
          )}
          <View style={styles.userDetails}>
            <ThemedText style={[styles.userName, { color: theme.text }]}>
              {request.user.name || 'Anonymous'}
            </ThemedText>
            {request.user.profession && (
              <ThemedText style={[styles.profession, { color: theme.textSecondary }]}>
                {request.user.profession}
              </ThemedText>
            )}
          </View>
        </View>
        <View style={styles.headerRight}>
          <ThemedText style={[styles.timestamp, { color: theme.textTertiary }]}>
            {getTimeAgo(request.createdAt)}
          </ThemedText>
          {showEditButton && onEdit && (
            <TouchableOpacity
              style={[styles.editButton, { backgroundColor: theme.backgroundSecondary }]}
              onPress={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="create-outline" size={18} color={theme.accent} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Business Name */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="business-outline" size={16} color={theme.accent} />
          <ThemedText style={[styles.sectionLabel, { color: theme.textSecondary }]}>
            Business
          </ThemedText>
        </View>
        <ThemedText style={[styles.businessTitle, { color: theme.text }]}>
          {request.businessName}
        </ThemedText>
      </View>

      {/* Business Details */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="information-circle-outline" size={16} color={theme.accent} />
          <ThemedText style={[styles.sectionLabel, { color: theme.textSecondary }]}>
            About
          </ThemedText>
        </View>
        <ThemedText
          style={[styles.description, { color: theme.text }]}
          numberOfLines={3}
        >
          {request.businessDetails}
        </ThemedText>
      </View>

      {/* Looking For */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="search-outline" size={16} color={theme.accent} />
          <ThemedText style={[styles.sectionLabel, { color: theme.textSecondary }]}>
            Looking For
          </ThemedText>
        </View>
        <ThemedText
          style={[styles.description, { color: theme.text }]}
          numberOfLines={3}
        >
          {request.lookingFor}
        </ThemedText>
      </View>

      {/* Submit Intro Button */}
      {showSubmitButton && (
        <TouchableOpacity
          style={[
            styles.submitButton,
            { 
              backgroundColor: theme.accent,
            }
          ]}
          onPress={onSubmitIntro}
          activeOpacity={0.7}
        >
          <Ionicons 
            name="add-circle-outline" 
            size={20} 
            color={theme.textInverse}
          />
          <ThemedText
            style={[
              styles.submitButtonText,
              { color: theme.textInverse }
            ]}
          >
            Pass Lead
          </ThemedText>
        </TouchableOpacity>
      )}

      {/* Intros Received Badge */}
      {showIntrosReceived && request.responses && request.responses.length > 0 && (
        <View style={[styles.introsBadge, { backgroundColor: theme.accent + '15', borderColor: theme.accent + '30' }]}>
          <View style={styles.introsBadgeContent}>
            <Ionicons name="arrow-forward-outline" size={16} color={theme.accent} />
            <ThemedText style={[styles.introsBadgeText, { color: theme.accent }]}>
              {request.responses.length} {request.responses.length === 1 ? 'Lead Received' : 'Leads Received'}
            </ThemedText>
          </View>
          <Ionicons name="chevron-forward" size={18} color={theme.accent} />
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  avatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userDetails: {
    marginLeft: 12,
    flex: 1,
  },
  userName: {
    fontSize: 15,
    fontWeight: '600',
  },
  profession: {
    fontSize: 12,
    marginTop: 2,
  },
  timestamp: {
    fontSize: 12,
    marginLeft: 8,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  editButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  section: {
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  businessTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    marginTop: 4,
    gap: 6,
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  introsBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 8,
    marginTop: 8,
    borderWidth: 1,
  },
  introsBadgeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  introsBadgeText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ReferralRequestCard;
